import { useCallback, useEffect, useState } from 'react'
import {
    buildStopTimerPatch,
    clampTimerSessionSeconds,
    elapsedSecondsSince,
    timerLogMinutes,
} from '../../lib/cardCompletion'

export default function useCardAutoTimer({
    t,
    card,
    effectiveTimerStartedAt,
    effectiveIsCompleted,
    setTimerOverride,
    addTimeLog,
    onUpdate,
    showAchievement,
    setUpdateError,
}) {
    const [activeTimer, setActiveTimer] = useState(0)
    const [trackedOverride, setTrackedOverride] = useState({ cardId: null, value: 0 })
    const baseTrackedSeconds = (trackedOverride.cardId === card?.id)
        ? trackedOverride.value
        : (Number(card?.tracked_seconds) || 0)

    useEffect(() => {
        let interval
        let cancelled = false
        if (effectiveTimerStartedAt && !effectiveIsCompleted) {
            const tick = () => {
                if (cancelled) return
                const start = new Date(effectiveTimerStartedAt).getTime()
                setActiveTimer(baseTrackedSeconds + clampTimerSessionSeconds(Math.floor((Date.now() - start) / 1000)))
            }
            tick()
            interval = setInterval(tick, 1000)
        } else {
            Promise.resolve().then(() => {
                if (!cancelled) setActiveTimer(baseTrackedSeconds)
            })
        }
        return () => {
            cancelled = true
            clearInterval(interval)
        }
    }, [effectiveTimerStartedAt, effectiveIsCompleted, baseTrackedSeconds])

    useEffect(() => {
        if (!effectiveTimerStartedAt || effectiveIsCompleted) return
        const autosaveInterval = setInterval(async () => {
            if (!effectiveTimerStartedAt) return
            const elapsedSec = clampTimerSessionSeconds(elapsedSecondsSince(effectiveTimerStartedAt))
            if (elapsedSec < 1) return
            const base = (trackedOverride.cardId === card.id) ? trackedOverride.value : (Number(card?.tracked_seconds) || 0)
            const restart = new Date().toISOString()
            setTrackedOverride({ cardId: card.id, value: base + elapsedSec })
            setTimerOverride({ cardId: card.id, value: restart })
            await onUpdate(card.id, { tracked_seconds: base + elapsedSec, timer_started_at: restart })
            const mins = timerLogMinutes(elapsedSec, { minSeconds: 30 })
            if (mins >= 1) await addTimeLog(mins)
        }, 5 * 60 * 1000)
        return () => clearInterval(autosaveInterval)
    }, [card?.id, card?.tracked_seconds, trackedOverride, effectiveTimerStartedAt, effectiveIsCompleted, addTimeLog, onUpdate, setTimerOverride])

    useEffect(() => {
        try { localStorage.removeItem('kanbify_unsaved_time') } catch { /* noop */ }
    }, [])

    const persistActiveTimer = useCallback(async () => {
        if (!effectiveTimerStartedAt) return { success: true }
        const base = (trackedOverride.cardId === card.id) ? trackedOverride.value : (Number(card?.tracked_seconds) || 0)
        const { elapsedSec, patch } = buildStopTimerPatch({
            timer_started_at: effectiveTimerStartedAt,
            tracked_seconds: card?.tracked_seconds,
        }, { baseTrackedSeconds: base })
        const elapsedMinutes = timerLogMinutes(elapsedSec)
        const logResult = await addTimeLog(elapsedMinutes)
        if (!logResult?.success) {
            const error = logResult?.error || t('card.timer.couldNotSaveSession')
            showAchievement({ type: 'error', message: t('card.timer.historyNotSaved', { error }) })
            setUpdateError(error)
            return { success: false, error }
        }
        const result = await onUpdate(card.id, patch)
        if (result?.success === false) {
            const error = result.error || t('card.timer.couldNotStop')
            setUpdateError(error)
            return { success: false, error }
        }
        setTrackedOverride({ cardId: card.id, value: base + elapsedSec })
        setTimerOverride({ cardId: card.id, value: null })
        return { success: true }
    }, [card, trackedOverride, effectiveTimerStartedAt, addTimeLog, onUpdate, showAchievement, t, setTimerOverride, setUpdateError])

    return { activeTimer, persistActiveTimer }
}
